import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RegistryOfficeItem } from 'src/app/api/models';
import { AssetManagementService } from 'src/app/api/services';

export interface SelectSerialsRowInput {
  // chiave con cui i seriali scelti vengono restituiti al chiamante
  key: number;
  product_id: number;
  name: string;
  brand?: string;
  stock_type: number;
  // numero di seriali da scegliere (uno per unità)
  qty: number;
}

interface SelectSerialsRow extends SelectSerialsRowInput {
  // i seriali impegnati (consegna senza DDT, o già evasi) sono mostrati ma non selezionabili
  options: { serial_number: string; label: string; disabled: boolean }[];
  selected: FormControl<string[]>;
  loading: boolean;
}

// Scelta dei seriali in giacenza per i prodotti serializzati che si stanno scaricando
@Component({
  selector: 'app-modal-select-serials',
  templateUrl: './modal-select-serials.component.html',
})
export class ModalSelectSerialsComponent {
  warehouse: RegistryOfficeItem;
  rows: SelectSerialsRow[];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private assetManagementService: AssetManagementService
  ) {
    this.warehouse = config.data.warehouse;
    this.rows = (config.data.rows as SelectSerialsRowInput[]).map(r => ({
      ...r,
      options: [],
      selected: new FormControl<string[]>([], { nonNullable: true }),
      loading: true,
    }));
    this.rows.forEach(r => this.loadOptions(r));
  }

  // Asset del prodotto nel magazzino con lo stesso utilizzo della giacenza. Sono scaricabili
  // solo quelli liberi: non impegnati in una consegna che non ha ancora un DDT, né già evasi
  private loadOptions(row: SelectSerialsRow) {
    this.assetManagementService
      .assetManagementControllerGetAll({
        limit: 500,
        skip: 0,
        related_product_id: row.product_id,
        related_warehouse_id: this.warehouse.id,
        purpose: row.stock_type,
      })
      .subscribe({
        next: res => {
          row.options = res.data.list.map(a => {
            // un DDT di trasferimento lascia l'asset in magazzino: si può scaricare
            const inDdt = !!a.related_ddt_id && !a.related_ddt_is_transfer;
            // il legame con la riga di consegna non conta: il rientro non lo azzera
            const inDelivery = !!a.related_delivery_id;
            const reason = inDdt
              ? ` (già evaso${a.related_ddt?.identifier ? ' con DDT ' + a.related_ddt.identifier : ''})`
              : inDelivery
                ? ` (impegnato nella consegna${a.related_delivery_id ? ' ' + a.related_delivery_id : ''}, senza DDT)`
                : '';
            return {
              serial_number: a.serial_number,
              label: a.serial_number + reason,
              disabled: inDdt || inDelivery,
            };
          });
          row.loading = false;
        },
        error: () => {
          row.loading = false;
        },
      });
  }

  availableCount(row: SelectSerialsRow): number {
    return row.options.filter(o => !o.disabled).length;
  }

  get canConfirm(): boolean {
    return this.rows.every(r => r.selected.value.length === r.qty);
  }

  close() {
    this.ref.close();
  }

  confirm() {
    if (!this.canConfirm) return;
    const serials: Record<number, string[]> = {};
    this.rows.forEach(r => (serials[r.key] = r.selected.value));
    this.ref.close({ serials });
  }
}
